import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { toMoneyString } from '@/lib/utils';
import type { BaseInstance } from '@pigello/pigello-matrix';
import { isNumber } from 'es-toolkit/compat';
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import type { Path } from 'react-hook-form';
import type { FieldProps } from '../types';
import { INTEGER_REGEXP, numberregexp, parseFieldValue } from '../utils';
import { DescriptionTooltip } from './description-tooltip';

type TO_INTERNAL_CALCULATION_METHODS = {
  1: (val: number) => number;
  2: (val: number, area: number | undefined) => number;
  3: (val: number) => number;
  4: (val: number, area: number | undefined) => number;
};

const TO_EXTERNAL_CALCULATION_METHODS = {
  1: (val: number) => val / 12, //YEARLY:1,
  2: (val: number, area: number | undefined) => (val * (area ?? 1)) / 12, //SQM_YEARLY:2,
  3: (val: number) => val, //MONTHLY:3,
  4: (val: number, area: number | undefined) => val * (area ?? 1), //SQM_MONTHLY:4
} as const satisfies TO_INTERNAL_CALCULATION_METHODS;

type Mode = keyof TO_INTERNAL_CALCULATION_METHODS;

const AREA_TOGGLE: Record<Mode, Mode> = { 1: 2, 2: 1, 3: 4, 4: 3 };
const PERIOD_TOGGLE: Record<Mode, Mode> = { 1: 3, 2: 4, 3: 1, 4: 2 };

const PriceInfo = ({ actualValue }: { actualValue: number | null }) => {
  if (actualValue == null || actualValue.toFixed(2) === 'NaN') {
    return null;
  }
  return (
    <div className='text-xs text-muted-foreground'>
      {toMoneyString(parseFloat(actualValue.toFixed(2)))}/mån,{' '}
      {toMoneyString(parseFloat((actualValue * 12).toFixed(2)))}/år
    </div>
  );
};

export interface PriceInputProps extends Omit<
  ComponentProps<typeof Input>,
  'value' | 'onChange'
> {
  /** The stored (monthly) value. */
  value: unknown;
  onChange: (next: number | null) => void;
  area?: number;
  integerOnly?: boolean;
  /** Called with the raw text the user typed, after sanitising. */
  onValueChange?: (value: unknown) => void;
}

/**
 * Form-agnostic price input: a text field plus toggles for per-year / per-month
 * and per-m² entry. The typed value is converted to a monthly amount before
 * `onChange` is called. Used by both the matrix `PriceField` and the construct
 * `priceField` kind.
 */
export function PriceInput({
  value,
  onChange,
  area,
  integerOnly,
  onValueChange,
  disabled,
  className,
  ...inputProps
}: PriceInputProps) {
  const [mode, setMode] = useState<Mode>(3);
  const [currentValue, setCurrentValue] = useState<string | null>(null);
  const selfWroteRef = useRef<number | null>(null);

  useEffect(() => {
    if (value == null || Number.isNaN(value)) {
      return;
    }
    if (value === selfWroteRef.current) {
      return;
    }
    if (String(value) === currentValue) {
      return;
    }
    setCurrentValue(String(value));
  }, [value, currentValue]);

  const setExternalValue = (parsed: string | null, overrideMode?: Mode) => {
    if (parsed) {
      const num = parseFloat(parsed);
      if (Number.isNaN(num)) {
        selfWroteRef.current = null;
        onChange(null);
        return;
      }
      const converterFunc = TO_EXTERNAL_CALCULATION_METHODS[overrideMode ?? mode];
      const rawExternalValue = converterFunc(num, area);
      const externalValue = integerOnly
        ? Math.round(rawExternalValue)
        : rawExternalValue;

      selfWroteRef.current = externalValue;
      onChange(externalValue);
    } else {
      selfWroteRef.current = null;
      onChange(null);
    }
  };

  const onUpdate = (internal: string | null, overrideMode?: Mode) => {
    const parsed = parseFieldValue({
      newValue: internal,
      prevValue: currentValue,
      regex: integerOnly ? INTEGER_REGEXP : numberregexp,
    });

    setCurrentValue(parsed);
    setExternalValue(parsed, overrideMode);
    onValueChange?.(parsed);
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    onUpdate(currentValue, next);
  };

  return (
    <div className='grid gap-1'>
      <div className='flex items-center'>
        <Input
          {...inputProps}
          className={cn('rounded-r-none', className)}
          onChange={(e) => onUpdate(e.target.value)}
          value={currentValue ?? ''}
          disabled={disabled}
        />

        {area != null && (
          <Button
            disabled={disabled}
            variant='outline'
            className={cn(
              'h-11 w-15 max-w-15 min-w-15 rounded-none border-l-0 py-3.25 text-xs leading-none! whitespace-nowrap shadow-none',
              {
                'opacity-50': [1, 3].includes(mode),
              }
            )}
            type='button'
            onClick={() => switchMode(AREA_TOGGLE[mode])}
          >
            /{area}m2
          </Button>
        )}
        <Button
          disabled={disabled}
          variant='secondary'
          className='h-11 w-15 max-w-15 min-w-15 rounded-input rounded-l-none border border-l-0 border-input py-3.25 text-xs leading-none! whitespace-nowrap shadow-none'
          type='button'
          onClick={() => switchMode(PERIOD_TOGGLE[mode])}
        >
          {[1, 2].includes(mode) ? '/år' : '/mån'}
        </Button>
      </div>
      <PriceInfo actualValue={isNumber(value) ? value : null} />
    </div>
  );
}

export function PriceField<Instance extends BaseInstance = BaseInstance>({
  formField,
  name,
  placeholder,
  label,
  description,
  className,
  horizontal,
  disabled,
  canHandleField,
  area,
  integerOnly,
  onValueChange,
}: FieldProps<Instance> & {
  area?: number;
  onValueChange?: (value: unknown) => void;
}) {
  return (
    <FormField
      rules={{
        required: formField.required && 'Detta fält är obligatoriskt',
        maxLength: {
          value: 20,
          message: 'Fältet får inte vara längre än 20 tecken',
        },
      }}
      control={formField.control}
      name={name as Path<Instance>}
      render={({ field }) => {
        const input = (
          <PriceInput
            ref={field.ref}
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={placeholder ?? formField.label}
            disabled={disabled ?? field.disabled ?? !canHandleField}
            area={area}
            integerOnly={integerOnly}
            onValueChange={onValueChange}
          />
        );

        if (horizontal) {
          return (
            <FormItem
              className={cn('flex items-center justify-between', className)}
            >
              <div className='w-1/2 shrink-0'>
                <div className='flex items-center'>
                  <FormLabel>
                    {label ?? formField.label}{' '}
                    {formField.required && <span>*</span>}
                  </FormLabel>
                  {(description || formField.description) && (
                    <DescriptionTooltip
                      description={description ?? formField.description}
                    />
                  )}
                </div>

                <FormMessage />
              </div>
              <FormControl>{input}</FormControl>
            </FormItem>
          );
        }

        return (
          <FormItem className={className}>
            <div className='flex items-center'>
              <FormLabel>
                {label ?? formField.label}{' '}
                {formField.required && <span>*</span>}
              </FormLabel>
              {(description || formField.description) && (
                <DescriptionTooltip
                  description={description ?? formField.description}
                />
              )}
            </div>
            <FormControl>{input}</FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
